'use client';
import { ThemeProvider } from '@mildang/mui';
import createCache, { Options } from '@emotion/cache';
import { isProject } from '@shared/constants/projectTarget';
import { useServerInsertedHTML } from 'next/navigation';
import { ReactNode, useMemo, useState } from 'react';
import { CacheProvider, ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import createTheme from '@mildang/design-system-legacy/esm/styles/createTheme';

// This implementation is from emotion-js
// https://github.com/emotion-js/emotion/issues/2928#issuecomment-1319747902
export default function ThemeRegistry(props: { options: Options; children: ReactNode }) {
  const { options, children } = props;

  const theme = useMemo(() => createTheme({ isMildangPTOnly: isProject('mildang') }), []);

  if (typeof window !== 'undefined') {
    const head = document.head;
    if (!head.querySelector('[data-styled="active"]')) {
      const injectlastNode = document.createElement('style');
      injectlastNode.setAttribute('data-styled', 'active');

      head.appendChild(injectlastNode);
    }
  }

  const [{ cache, flush }] = useState(() => {
    const cache = createCache(options);
    cache.compat = true;
    const prevInsert = cache.insert;
    let inserted: string[] = [];
    cache.insert = (...args) => {
      const serialized = args[1];
      if (cache.inserted[serialized.name] === undefined) {
        inserted.push(serialized.name);
      }
      return prevInsert(...args);
    };
    const flush = () => {
      const prevInserted = inserted;
      inserted = [];
      return prevInserted;
    };
    return { cache, flush };
  });

  useServerInsertedHTML(() => {
    const names = flush();
    if (names.length === 0) {
      return null;
    }
    let styles = '';
    for (const name of names) {
      styles += cache.inserted[name];
    }
    return (
      <style
        key={cache.key}
        data-emotion={`${cache.key} ${names.join(' ')}`}
        dangerouslySetInnerHTML={{
          __html: styles,
        }}
      />
    );
  });

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <EmotionThemeProvider theme={theme}>{children}</EmotionThemeProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}
